// Convert parsed bank rows into balanced double-entry journals using the extraction engine (tool-use).
// Returns an array of { date, narration, source, lines: [{accountCode, dr, cr}], confidence, sourceRow }.

import { authHeader } from './firebase';

const MODEL = 'claude-sonnet-5';
// Statement TRANSCRIPTION is pure copying — list every line, no accounting judgment —
// so it runs on the smaller/faster/cheaper Haiku model. Categorisation (which needs the
// COA reasoning) stays on MODEL. Haiku 4.5 is ~3x cheaper per token than Sonnet, so this
// cuts latency AND cost, not just latency.
const TRANSCRIBE_MODEL = 'claude-haiku-4-5';
// All calls go through our own backend so the provider key is never in the browser.
const ENGINE_URL = (process.env.REACT_APP_ENGINE_URL ?? 'http://localhost:3001') + '/api/engine';

// POST a request body to our engine, retrying on rate limits (429) so multi-chunk
// jobs ride out the per-minute input-token limit instead of failing.
async function postToEngine(body, retries) {
  const max = retries == null ? 3 : retries;
  for (let attempt = 0; ; attempt++) {
    const res = await fetch(ENGINE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
      body: JSON.stringify(body),
    });
    if (res.status === 429 && attempt < max) {
      await new Promise((r) => setTimeout(r, 30000)); // wait for the per-minute window to reset
      continue;
    }
    if (!res.ok) {
      const err = await res.text();
      throw new Error('API ' + res.status + ': ' + err.slice(0, 400));
    }
    return res.json();
  }
}
const POST_TOOL = {
  name: 'post_journals',
  description: 'Submit a list of balanced double-entry journal entries derived from bank statement transactions.',
  input_schema: {
    type: 'object',
    properties: {
      entries: {
        type: 'array',
        description: 'Journal entries. Each entry must balance: sum(lines.dr) === sum(lines.cr).',
        items: {
          type: 'object',
          properties: {
            sourceRowIndex: { type: 'integer', description: 'Zero-based index of the bank row this entry corresponds to.' },
            date: { type: 'string', description: 'ISO date YYYY-MM-DD.' },
            narration: { type: 'string', description: 'Concise human-readable description of the journal.' },
            lines: {
              type: 'array',
              minItems: 2,
              items: {
                type: 'object',
                properties: {
                  accountCode: { type: 'string', description: 'GL code from the chart of accounts.' },
                  accountName: { type: 'string', description: 'Account name copied from the chart of accounts.' },
                  dr: { type: 'number', description: 'Debit amount (>= 0).' },
                  cr: { type: 'number', description: 'Credit amount (>= 0).' },
                },
                required: ['accountCode', 'accountName', 'dr', 'cr'],
              },
            },
            confidence: { type: 'string', enum: ['high', 'medium', 'low'] },
            needsReview: { type: 'boolean', description: 'True if a human accountant should double-check this entry.' },
            notes: { type: 'string', description: 'Optional explanation of categorisation choice or flagged concerns.' },
          },
          required: ['sourceRowIndex', 'date', 'narration', 'lines', 'confidence'],
        },
      },
    },
    required: ['entries'],
  },
};

function buildSystem(project, coa, bankAccountCode) {
  const coaLines = coa.map((a) => '  ' + a.code + '  ' + a.name + '  (' + a.type + ')').join('\n');
  const orientation =
    'You are Tlhakiso, a South African accounting assistant performing double-entry bookkeeping.\n' +
    'Convert each bank statement row into ONE balanced journal entry.\n\n' +
    'COMPANY: ' + project.name + (project.type ? ' (' + project.type + ')' : '') + '\n' +
    (project.yearStart && project.yearEnd ? 'FINANCIAL YEAR: ' + project.yearStart + ' to ' + project.yearEnd + '\n' : '') +
    'TAX: ' + (project.taxType || 'Non-SBC') + ' at ' + (project.taxRate || 27) + '%\n' +
    'BANK ACCOUNT FOR THIS UPLOAD: ' + bankAccountCode + '\n\n' +
    'CHART OF ACCOUNTS (use ONLY these codes):\n' + coaLines + '\n\n' +
    'RULES:\n' +
    '1. Every entry MUST balance: sum of dr = sum of credits.\n' +
    '2. Money IN to the bank → debit ' + bankAccountCode + ', credit the income/liability/equity contra account.\n' +
    '3. Money OUT of the bank → credit ' + bankAccountCode + ', debit the expense/asset/liability contra account.\n' +
    '4. Inter-account transfers between the company\'s own bank accounts are NOT income/expense — debit one bank, credit the other.\n' +
    '5. Loans from shareholders or third parties (deposits) → credit the liability (Shareholder\'s Loan / Third-Party Loan). Repayments → debit the liability.\n' +
    '6. Food, entertainment, ATM cash withdrawals = non-deductible expenses but still go to their expense account; flag needsReview=false unless ambiguous.\n' +
    '7. Bank fees → Bank Charges (610380). Interest received → Interest Income (500020).\n' +
    '8. Use 2 decimal places. Amounts must be positive in dr or cr (never negative).\n' +
    '9. If the categorisation is uncertain or the description is cryptic, set needsReview=true and pick the best guess.\n' +
    '10. The sourceRowIndex MUST match the row index you were given.\n' +
    'Call the post_journals tool exactly once with all entries.';
  return orientation;
}

// PDF variant of the tool — no row index; the engine reads the PDF directly and fills in source description + amount per entry.
const POST_TOOL_PDF = {
  name: 'post_journals',
  description: 'Submit a list of balanced double-entry journal entries derived from a bank statement PDF.',
  input_schema: {
    type: 'object',
    properties: {
      entries: {
        type: 'array',
        description: 'Journal entries. Each entry must balance: sum(lines.dr) === sum(lines.cr).',
        items: {
          type: 'object',
          properties: {
            sourceDescription: { type: 'string', description: 'The bank statement line description, verbatim from the PDF.' },
            sourceAmount: { type: 'number', description: 'The signed amount from the statement: positive = money in (credit on the statement), negative = money out (debit on the statement).' },
            date: { type: 'string', description: 'ISO date YYYY-MM-DD.' },
            narration: { type: 'string', description: 'Concise human-readable description of the journal.' },
            lines: {
              type: 'array',
              minItems: 2,
              items: {
                type: 'object',
                properties: {
                  accountCode: { type: 'string' },
                  accountName: { type: 'string' },
                  dr: { type: 'number' },
                  cr: { type: 'number' },
                },
                required: ['accountCode', 'accountName', 'dr', 'cr'],
              },
            },
            confidence: { type: 'string', enum: ['high', 'medium', 'low'] },
            needsReview: { type: 'boolean' },
            notes: { type: 'string' },
          },
          required: ['sourceDescription', 'sourceAmount', 'date', 'narration', 'lines', 'confidence'],
        },
      },
    },
    required: ['entries'],
  },
};

export async function extractJournalsFromPdf({ project, coa, bankAccountCode, pdfBase64, fileName }) {
  if (!pdfBase64) throw new Error('No PDF data provided');

  const data = await postToEngine({
    model: MODEL,
    max_tokens: 24000,
    system: buildSystem(project, coa, bankAccountCode) +
      '\n\nADDITIONAL INSTRUCTIONS FOR PDF STATEMENTS:\n' +
      '- The user has supplied a PDF of the bank statement. Extract EVERY individual transaction line from it.\n' +
      '- Do not skip lines because they look small (bank fees, interest) or because they seem repetitive.\n' +
      '- Do not include subtotals, opening balance, or closing balance rows as transactions — those are not journals.\n' +
      '- For each transaction line you find, populate sourceDescription with the exact description text from the PDF, and sourceAmount with the signed amount (positive = money in, negative = money out).\n' +
      '- The PDF may show debit and credit in separate columns; convert to a single signed amount on sourceAmount.',
    tools: [POST_TOOL_PDF],
    tool_choice: { type: 'tool', name: 'post_journals' },
    messages: [
      {
        role: 'user',
        content: [
          { type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: pdfBase64 } },
          {
            type: 'text',
            text:
              'Process this bank statement PDF (' + (fileName || 'statement.pdf') + ') into balanced double-entry journals using the chart of accounts. ' +
              'Return one journal per transaction line via the post_journals tool.',
          },
        ],
      },
    ],
  });

  if (data.stop_reason === 'max_tokens') {
    throw new Error('The statement was too large and the response was truncated (hit the output token limit). Split it into smaller date ranges or upload it as CSV/Excel, then try again — do not post a partial result.');
  }
  const toolUse = (data.content || []).find((b) => b.type === 'tool_use' && b.name === 'post_journals');
  if (!toolUse) {
    throw new Error('The extraction engine returned an unexpected response. Please try again. ' + JSON.stringify(data).slice(0, 200));
  }
  const entries = (toolUse.input && toolUse.input.entries) || [];
  // Shape into the same structure as the CSV/XLSX path so the review UI is identical.
  const journals = entries.map((e) => ({
    date: e.date,
    narration: e.narration,
    confidence: e.confidence,
    needsReview: e.needsReview,
    notes: e.notes,
    lines: e.lines,
    sourceRow: {
      date: e.date,
      description: e.sourceDescription || '',
      amount: typeof e.sourceAmount === 'number' ? e.sourceAmount : 0,
      balance: null,
    },
  }));
  return { journals, rawResponse: data };
}

export async function extractJournalsFromBankRows({ project, coa, bankAccountCode, rows }) {
  if (!rows || rows.length === 0) return { journals: [], rawResponse: null };

  const userPayload = {
    bank_account: bankAccountCode,
    transactions: rows.map((r, i) => ({
      index: i,
      date: r.date,
      description: r.description,
      amount: r.amount,
      balance: r.balance,
    })),
  };

  const data = await postToEngine({
    model: MODEL,
    max_tokens: 24000,
    system: buildSystem(project, coa, bankAccountCode),
    tools: [POST_TOOL],
    tool_choice: { type: 'tool', name: 'post_journals' },
    messages: [
      {
        role: 'user',
        content:
          'Process these bank transactions into balanced double-entry journals using the chart of accounts. ' +
          'Return one journal per row via the post_journals tool.\n\n' +
          JSON.stringify(userPayload, null, 2),
      },
    ],
  });

  if (data.stop_reason === 'max_tokens') {
    throw new Error('The statement was too large and the response was truncated (hit the output token limit). Split it into smaller date ranges or upload it as CSV/Excel, then try again — do not post a partial result.');
  }
  const toolUse = (data.content || []).find((b) => b.type === 'tool_use' && b.name === 'post_journals');
  if (!toolUse) {
    throw new Error('The extraction engine returned an unexpected response. Please try again. ' + JSON.stringify(data).slice(0, 200));
  }
  const entries = (toolUse.input && toolUse.input.entries) || [];
  // Attach the original row so the review UI can show the source description.
  const journals = entries.map((e) => ({
    ...e,
    sourceRow: rows[e.sourceRowIndex] || null,
  }));
  return { journals, rawResponse: data };
}

// ── PDF → rows (transcription only) ──
// Turn the raw text layer of a statement into the SAME normalised rows that
// csvParser produces, so a PDF flows through the reliable row path + review UI.
// This is pure transcription (list every line) — no accounting — which the model
// does far more reliably than reading a PDF image and categorising in one shot.
const ROWS_TOOL = {
  name: 'submit_rows',
  description: 'Submit every transaction line transcribed from a bank statement, in order.',
  input_schema: {
    type: 'object',
    properties: {
      rows: {
        type: 'array',
        description: 'One object per transaction line. Exclude opening/closing balance, brought-forward and subtotal lines.',
        items: {
          type: 'object',
          properties: {
            date: { type: 'string', description: 'ISO date YYYY-MM-DD.' },
            description: { type: 'string', description: 'The transaction description, verbatim from the statement.' },
            amount: { type: 'number', description: 'Signed amount: positive = money IN (credit), negative = money OUT (debit).' },
            balance: { type: 'number', description: 'Running balance if shown; omit if not present.' },
          },
          required: ['date', 'description', 'amount'],
        },
      },
    },
    required: ['rows'],
  },
};

// One transcription pass over a chunk of statement text.
async function transcribeStatementChunk(text, sourceLabel, period) {
  const data = await postToEngine({
    model: TRANSCRIBE_MODEL,
    max_tokens: 24000,
    system:
      'You transcribe a South African bank statement into structured transaction rows. ' +
      'You are NOT doing accounting — only faithfully listing every transaction line exactly as printed.\n' +
      'RULES:\n' +
      '1. Output ONE row per transaction line, in the order they appear.\n' +
      '2. Include EVERY line — bank fees, interest, tiny amounts, repeated entries. Never skip or merge lines.\n' +
      '3. Exclude opening balance, closing balance, brought-forward and subtotal rows (they are not transactions).\n' +
      '4. amount is a single SIGNED number: positive = money in, negative = money out. Merge any separate debit/credit columns into this one signed value.\n' +
      '5. Dates as ISO YYYY-MM-DD. If a line omits the year, infer it from the statement period' + (period ? ' (' + period + ')' : '') + '.\n' +
      '6. Copy the description text verbatim (do not summarise).\n' +
      'Call submit_rows exactly once with all rows.',
    tools: [ROWS_TOOL],
    tool_choice: { type: 'tool', name: 'submit_rows' },
    messages: [
      { role: 'user', content: 'Bank statement text (' + (sourceLabel || 'PDF') + '):\n\n' + text },
    ],
  });
  if (data.stop_reason === 'max_tokens') {
    throw new Error('A section of the statement was too dense to transcribe in one pass. Split it into smaller date ranges and try again.');
  }
  const toolUse = (data.content || []).find((b) => b.type === 'tool_use' && b.name === 'submit_rows');
  if (!toolUse) {
    throw new Error('Could not transcribe the statement text. ' + JSON.stringify(data).slice(0, 200));
  }
  return ((toolUse.input && toolUse.input.rows) || []).map((r) => ({
    date: r.date,
    description: (r.description || '').trim(),
    amount: typeof r.amount === 'number' ? r.amount : 0,
    balance: typeof r.balance === 'number' ? r.balance : null,
  }));
}

// Turn statement text into normalised rows. Large multi-month statements are
// split on line boundaries and transcribed in parts (then merged in order) so a
// single transcription never hits the output-token cap.
export async function extractRowsFromStatementText({ text, sourceLabel, period }) {
  if (!text || !text.trim()) return { rows: [] };
  const CHUNK_CHARS = 12000;
  if (text.length <= CHUNK_CHARS) {
    return { rows: await transcribeStatementChunk(text, sourceLabel, period), chunked: 1 };
  }
  const lines = text.split('\n');
  const chunks = [];
  let cur = '';
  for (const ln of lines) {
    if (cur && cur.length + ln.length + 1 > CHUNK_CHARS) { chunks.push(cur); cur = ''; }
    cur += ln + '\n';
  }
  if (cur.trim()) chunks.push(cur);
  // Transcribe chunks with bounded concurrency instead of one-at-a-time. Each chunk
  // is an independent LLM round-trip, so running a few at once cuts wall-clock on long
  // statements without changing token cost. Results are stored by index so the merged
  // rows stay in statement order. Cap kept low so we stay under the engine's per-minute
  // rate limit (postToEngine already backs off on 429).
  const CONCURRENCY = 3;
  const parts = new Array(chunks.length);
  let nextIndex = 0;
  async function worker() {
    for (;;) {
      const i = nextIndex++;
      if (i >= chunks.length) return;
      parts[i] = await transcribeStatementChunk(
        chunks[i], (sourceLabel || 'PDF') + ' part ' + (i + 1) + '/' + chunks.length, period);
    }
  }
  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, chunks.length) }, worker));
  const rows = [];
  for (const part of parts) rows.push(...part);
  return { rows, chunked: chunks.length };
}

// Validate that every entry balances and uses real account codes.
// Returns array of warning strings (empty = clean).
export function validateJournals(journals, coa) {
  const codes = new Set(coa.map((a) => a.code));
  const warnings = [];
  journals.forEach((j, i) => {
    const dr = (j.lines || []).reduce((s, l) => s + (Number(l.dr) || 0), 0);
    const cr = (j.lines || []).reduce((s, l) => s + (Number(l.cr) || 0), 0);
    if (Math.abs(dr - cr) > 0.01) {
      warnings.push('Entry ' + (i + 1) + ' (' + (j.narration || '') + ') does not balance: dr=' + dr.toFixed(2) + ' cr=' + cr.toFixed(2));
    }
    (j.lines || []).forEach((l, li) => {
      if (!codes.has(l.accountCode)) {
        warnings.push('Entry ' + (i + 1) + ' line ' + (li + 1) + ': unknown account code "' + l.accountCode + '"');
      }
      if ((Number(l.dr) || 0) < 0 || (Number(l.cr) || 0) < 0) {
        warnings.push('Entry ' + (i + 1) + ' line ' + (li + 1) + ': negative amount');
      }
      if ((Number(l.dr) || 0) > 0 && (Number(l.cr) || 0) > 0) {
        warnings.push('Entry ' + (i + 1) + ' line ' + (li + 1) + ': line has both debit and credit (split into two lines)');
      }
    });
  });
  return warnings;
}
